/**
 * LibraryLink — routes a shelved library entry (guide or playbook) to its
 * page. The path is derived from the entry's content-type discriminant, never
 * stored or passed in as a raw href — mirrors how `$guideSlug.tsx` resolves
 * playbook-vs-guide routing. Shared by both `/coaching-business` and
 * `/coaching-business/playbooks`, which both render library shelves built by
 * `~/lib/shared/cih/library-shelves`, and by the coach's Grow your practice
 * page, which opens them in a new tab because they leave the app.
 *
 * Aliases: library card link, shelf entry link, guide or playbook link
 */

import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

export type LibraryLinkEntry =
  | { readonly type: "guide"; readonly item: { readonly slug: string } }
  | { readonly type: "playbook"; readonly item: { readonly slug: string } };

export function LibraryLink({
  entry,
  className,
  newTab = false,
  children,
}: {
  readonly entry: LibraryLinkEntry;
  readonly className?: string;
  readonly newTab?: boolean;
  readonly children: ReactNode;
}) {
  const tab = newTab
    ? ({ target: "_blank", rel: "noopener" } as const)
    : undefined;
  if (entry.type === "guide") {
    return (
      <Link
        to="/coaching-business/$guideSlug"
        params={{ guideSlug: entry.item.slug }}
        className={className}
        {...tab}
      >
        {children}
      </Link>
    );
  }
  return (
    <Link
      to="/coaching-business/playbooks/$slug"
      params={{ slug: entry.item.slug }}
      className={className}
      {...tab}
    >
      {children}
    </Link>
  );
}
